<template>
  <div class="home-container">
    <vue-recaptcha
      ref="recaptcha"
      :sitekey="siteKey"
      size="invisible"
      @verify="register"
      @expired="onCaptchaExpired"
    />
    <b-modal
      id="verify-2fa-modal"
      :active.sync="isShowVerifyModal"
      scroll="keep"
    >
      <Verify2FAModal
        ref="verify2FAModal"
        class="modal-card"
        @submit="onSubmitVerify2FA"
      />
    </b-modal>
    <div class="home-container-item ">
      <Header/>
    </div>
    <div class="section-gray">
      <div class="register-title">
        {{ $t('register.register') }}
      </div>
      <div class="section-white">
        <div class="social-login-container">
          <FacebookLoginButton
            :text="$t('register.login_with', ['Facebook'])"
            class="facebook-login-button"
            @onSignInSuccess="onSignInFacebookSuccess"
            @onSignInError="onSignInFacebookError"
          />
          <GoogleLoginButton
            :text="$t('register.login_with', ['Google'])"
            class="google-login-button"
            @onSignInSuccess="onSignInGoogleSuccess"
            @onSignInError="onSignInGoogleError"
          />
        </div>
        <div class="divider">
          <div class="or">{{ $t('register.or') }}</div>
        </div>
        <div
          id="email"
          class="is-expanded register-field-box"
          data-test="register--email-field"
        >
          <b-field :label="$t('register.email') + ':'">
            <b-input
              v-validate="'required|email'"
              v-model="form.email"
              :disabled="isLoading"
              class="is-underline-input-white is-underline-input-gray"
              placeholder="For exampler abc@gmail.com"
              name="email"
              type="text"
              @blur="trackInputForm({ key: 'email', name: 'email'})"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('email') }}</span>
        </div>
        <div
          id="password"
          class="is-expanded register-field-box"
          data-test="register--password-field"
        >
          <b-field
            :label="$t('register.password') + ':'"
            class="register-password-field"
          >
            <span class="register-field-note-text">
              {{ $t('register.password_condition') }}
            </span>
            <b-input
              v-validate="{ required: true, max: 120, regex: /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9]).{8,}$/ }"
              v-model="form.password"
              :disabled="isLoading"
              :placeholder="$t('register.password_placeholder')"
              class="is-underline-input-white is-underline-input-gray w-100pct"
              name="password"
              type="password"
              password-reveal
              @blur="trackInputForm({ key: 'password', name: 'password'})"
            />
          </b-field>
          <PasswordMeter
            v-model="form.password"
            :secure-length="8"
            strength-meter-only
            class="password-meter"
            @score="onPasswordScoreChange"
          />
          <span
            v-if="passwordMeterLabel"
            :class="['password-meter--score', {
              'very-weak': passwordScore === 0,
              'weak': passwordScore === 1,
              'normal': passwordScore === 2,
              'strong': passwordScore === 3,
              'very-strong': passwordScore === 4
            }]"
          >
            {{ $t(`register.password_meter_${passwordScore}`) }}
          </span>
          <div class="is-invalid">{{ errors.first('password') }}</div>
        </div>
        <div
          id="confirm password"
          class="is-expanded register-field-box"
          data-test="register--confirm-password-field"
        >
          <b-field :label="$t('register.confirm_password') + ':'">
            <b-input
              v-validate="{ required: true, is: form.password, max: 120 }"
              v-model="form.confirmPassword"
              :disabled="isLoading"
              :placeholder="$t('register.confirm_password_placeholder')"
              class="is-underline-input-white is-underline-input-gray"
              name="confirm password"
              type="password"
              password-reveal
              @blur="trackInputForm({ key: 'confirmPassword', name: 'confirm password'})"
            />
          </b-field>
          <div class="is-invalid">{{ errors.first('confirm password') }}</div>
        </div>
        <div class="register-field-box">
          <div class="field">
            <b-checkbox
              v-model="form.isSubscribeNews"
              :disabled="isLoading"
              type="is-info"
            >
              {{ $t('register.subscribe') }}
            </b-checkbox>
          </div>
        </div>
        <div
          id="accept terms"
          class="register-field-box"
        >
          <div
            class="field"
            data-test="register--accept-terms-checkbox"
          >
            <b-checkbox
              v-validate="'required:false'"
              v-model="form.isAcceptTerms"
              :disabled="isLoading"
              name="accept terms"
              type="is-info"
            >
              {{ $t('register.agree_to') }}
              <a @click="openLinkNewTab('Terms')">{{ $t('register.infor_before_regis') }}</a>
              {{ $t('register.and') }}
              <a @click="openLinkNewTab('PrivacyPolicy')">{{ $t('register.privacy_policy') }}</a>
              {{ $t('register.and') }}
              <a @click="openLinkNewTab('AMLPolicy')">{{ $t('register.aml_policy') }}</a>
            </b-checkbox>
          </div>
          <span class="is-invalid">{{ errors.first('accept terms') }}</span>
        </div>
        <div class="register-field-note-text t-al-left">
          {{ $t('register.if_you_have_question') }}
        </div>
        <div class="register-field-note-text t-al-left">
          {{ $t('register.please_contact') }}
        </div>
        <b-button
          :loading="isLoading"
          class="register-submit-button"
          data-test="register--submit-button"
          @click="executeRecaptcha()"
        >
          {{ $t('register.register_uppercase') }}
        </b-button>
      </div>
    </div>
    <div class="home-container-item">
      <Footer/>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import PasswordMeter from 'vue-password-strength-meter'
import Header from '@/features/Header'
import Footer from '@/components/Footer'
import FacebookLoginButton from '@/components/FacebookLoginButton'
import GoogleLoginButton from '@/components/GoogleLoginButton'
import Verify2FAModal from '@/components/Verify2FAModal'
import RequestEmailModal from '@/components/RequestEmailModal'
import CongratsToGetReward from '@/components/CongratsToGetReward'
import Service from './service.js'
import cookie from '@/utils/cookie'
import AmplitudeService from '@/services/amplitude'
import { kulapApi } from '@/resources'
import { calTotalKULToken, showToastError } from '@/utils/helper'
import { mapState } from 'vuex'
import { ENV, RECAPTCHA_SITE_KEY } from '@/constants'

export default {
  name: 'Register',
  components: {
    Header,
    Footer,
    FacebookLoginButton,
    GoogleLoginButton,
    Verify2FAModal,
    RequestEmailModal,
    VueRecaptcha,
    PasswordMeter
  },
  data () {
    return {
      service: null,
      form: {
        email: '',
        password: '',
        confirmPassword: '',
        isSubscribeNews: true,
        isAcceptTerms: false
      },
      siteKey: RECAPTCHA_SITE_KEY,
      passwordScore: null,
      isShowVerifyModal: false,
      isLoading: false,
      twoFAData: {}
    }
  },
  computed: {
    ...mapState({
      user: state => state.user
    }),
    passwordMeterLabel () {
      switch (this.passwordScore) {
        case 0: return 'Very weak'
        case 1: return 'Weak'
        case 2: return 'Normal'
        case 3: return 'Strong'
        case 4: return 'Very strong'
        default: return ''
      }
    }
  },
  created () {
    if (cookie.get('user') || cookie.get('accessToken')) {
      this.$router.push({ name: 'Exchange' })
    }
    this.service = new Service({
      store: this.$store,
      route: this.$route,
      kulapApi
    })
    this.service.setReferralCode()
  },
  methods: {
    async onSignInFacebookSuccess (response) {
      try {
        const signInData = await this.service.signInWithFacebook(response.authResponse.userID, response.authResponse.accessToken)
        if (signInData && signInData.requestEmail) {
          this.openRequestEmailModal()
        } else if (signInData && signInData.secretKeyForVerify2FA) {
          this.isShowVerifyModal = true
          this.twoFAData = signInData
        } else {
          await this.congratsAfterRegister()
          this.$router.push({ name: 'Exchange' })
        }
      } catch (e) {
        showToastError(e)
      }
    },
    onSignInFacebookError (e) {
      if (e && e.status !== 'unknown') showToastError({ message: `Sorry, something went wrong.` })
    },
    async onSignInGoogleSuccess (googleUser) {
      try {
        const signInData = await this.service.signInWithGoogle(googleUser)
        if (signInData && signInData.secretKeyForVerify2FA) {
          this.isShowVerifyModal = true
          this.twoFAData = signInData
        } else {
          await this.congratsAfterRegister()
          this.$router.push({ name: 'Exchange' })
        }
      } catch (e) {
        showToastError(e)
      }
    },
    onSignInGoogleError (e) {
      if (e.error && e.error !== 'popup_closed_by_user') showToastError({ message: `Sorry, something went wrong.` })
    },
    openRequestEmailModal () {
      let onRequestEmailModal = this.$buefy.modal.open({
        component: RequestEmailModal,
        hasModalCard: true,
        parent: this,
        events: {
          getEmail: async (email, recaptchaToken) => {
            try {
              await this.service.updateUserEmail(email, recaptchaToken)
              onRequestEmailModal.close()
              this.$router.push({name: 'RegisterStatus', query: {opts: 'facebookNoEmail'}})
            } catch (e) {
              showToastError(e)
            }
          }
        },
        canCancel: ['escape', 'outside', 'x']
      })
    },
    async executeRecaptcha () {
      const isEmailValid = await this.$validator.validate('email')
      if (!isEmailValid) {
        this.$scrollTo(document.getElementById('email'))
        return
      }
      const isPasswordValid = await this.$validator.validate('password')
      if (!isPasswordValid) {
        this.$scrollTo(document.getElementById('password'))
        return
      }
      const isConfirmPasswordValid = await this.$validator.validate('confirm password')
      if (!isConfirmPasswordValid) {
        this.$scrollTo(document.getElementById('confirm password'))
        return
      }
      const isTermsValid = await this.$validator.validate('accept terms')
      if (!isTermsValid) {
        this.$scrollTo(document.getElementById('accept terms'))
        return
      }
      if (this.form.password === this.form.confirmPassword && this.form.isAcceptTerms) {
        if (!window.Cypress) {
          this.isLoading = true
          this.$refs.recaptcha.execute()
        } else {
          this.register()
        }
      }
    },
    async register (recaptchaToken) {
      try {
        if (window.Cypress && ['develop', 'development', 'alpha'].includes(ENV)) {
          recaptchaToken = window.Cypress.env().RECAPTCHA_TEST_TOKEN
        }
        await this.service.registerNewUserViaEmail(this.form, recaptchaToken)
        this.$router.push({ name: 'RegisterStatus' })
        this.$scrollTo('.home-container-item')
      } catch (e) {
        showToastError(e)
      }
      this.$refs.recaptcha.reset()
      this.isLoading = false
    },
    async onSubmitVerify2FA (code) {
      try {
        const result = await this.service.verify2FACode(code, this.twoFAData.secretKeyForVerify2FA, this.twoFAData.userId)
        if (Object.keys(result).length) {
          await this.service.setUserAuthCookies(result)
          this.isShowVerifyModal = false
          await this.congratsAfterRegister()
          this.$router.push({ name: 'Exchange' })
        }
      } catch (e) {
        this.$refs.verify2FAModal.updateIsLoading(false)
        showToastError(e)
      }
    },
    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
      this.isLoading = false
    },
    onPasswordScoreChange (score) {
      this.passwordScore = score
    },
    async congratsAfterRegister () {
      const data = this.user
      if (data.rewards.length) {
        const filterRewards = data.rewards.filter(e => {
          return [ 'Registration', 'SubscribeNewsLetter', 'ConnectWithGoogle', 'ConnectWithFacebook' ].includes(e.name) && e.options.isOpenCongratsModal
        })
        if (filterRewards.length) {
          this.$buefy.modal.open({
            parent: this,
            component: CongratsToGetReward,
            hasModalCard: true,
            props: {
              tokenAmount: calTotalKULToken(filterRewards),
              subTitle: this.$t('messages.thanks_for_registration')
            },
            canCancel: ['escape', 'outside']
          })
          const changeRewardsOption = filterRewards.map(e => ({ ...e, options: { isOpenCongratsModal: false } }))
          await kulapApi.putRewardsOption(changeRewardsOption)
        }
      }
    },
    trackInputForm ({ key, name }) {
      if (this.form[key]) {
        const isValid = this.fields[name] && this.fields[name].valid
        AmplitudeService.inputField(key, isValid, 'EVENT_REGISTER_FLOW')
      }
    },
    openLinkNewTab (name) {
      let routeData = ''
      if (name === 'Terms') {
        routeData = this.$router.resolve({ name: 'Terms' })
      } else if (name === 'PrivacyPolicy') {
        routeData = {
          href: 'https://www.kulap.io/policy'
        }
      } else if (name === 'AMLPolicy') {
        routeData = {
          href: 'https://www.kulap.io/aml'
        }
      }
      window.open(routeData.href, '_blank')
    }
  }
}
</script>

<style lang="scss">
#verify-2fa-modal {
  .modal-content {
    overflow: hidden;
  }
  .modal-card {
    background-color: #fff;
    border-radius: 5px;
    padding: 30px 40px;
  }
}
</style>

<style lang="scss" scoped>
.home-container {
  float: left;
  width: 100%;
  min-height: 100vh;
}
.home-container-item {
  float: left;
  width: 100%;
}
.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 170px;
  text-align: center;
}
.register-title {
  margin: 35px 0;
  font-size: 30px;
  font-weight: bold;
  text-align: center;
  color: #000000;
}
.section-white {
  display: inline-block;
  padding: 65px 100px;
  max-width: 800px;
  border-radius: 5px;
  box-shadow: 0 2px 4px -3px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
  text-align: center;
}
.social-login-container {
  display: inline-block;
  width: 100%;
}
.google-login-button {
  float: left;
  margin-left: 15px;
  width: 265px;
}
.facebook-login-button {
  float: left;
  margin-right: 15px;
  width: 265px;
}
.divider {
  margin-top: 20px;
  margin-bottom: 30px;
  border-top: 1px solid #d5d5d5;
  height: 0;
  width: 100%;
  position: relative;
}
.or {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translate(-25px);
  background-color: #fff;
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #4d4d4d;
}
.register-field-box {
  text-align: left;
  margin-bottom: 30px;
}
.register-password-field {
  flex-wrap: wrap;
}
.register-field-note-text {
  font-size: 12px;
  letter-spacing: 0.3px;
  color: #939393;
  margin-left: 10px;
  line-height: 24px;

  &--hightlight {
    font-weight: bold;
    color: #ff4500;
  }
}
.captcha-input {
  width: 243px;
}
.t-al-left {
  text-align: left;
}
.w-100pct {
  width: 100%;
}
.register-submit-button {
  display: flex;
  justify-content: center;
  border-radius: 5px;
  border: unset;
  background-color: #2193ff;
  float: left;
  width: 100%;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.4px;
  color: #ffffff;
  padding: 23px 13px;
  margin-top: 40px;
  cursor: pointer;
}
.password-meter {
  margin: 0;

  &--score {
    font-size: 13px;
    font-weight: bold;
  }
}
.very-weak {
  color: darkred
}
.weak {
  color: #ff4500
}
.normal {
  color: orange
}
.strong {
  color: #9acd32
}
.very-strong {
  color: green
}
.modal-card {
  margin: 0 auto !important;
}
/* Less than tablet */
@media only screen and (max-width: 1087px) {
  .section-white {
    width: 90vw;
    max-width: calc(100vw - 20px);
  }
  .facebook-login-button {
    width: 49%;
    margin: 0;
  }
  .google-login-button {
    width: 49%;
    margin: 0 0 0 1%;
  }
  #verify-2fa-modal {
    min-width: 0;
    margin: 0 auto;
  }
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .modal-card {
    width: 90vw;
    padding: 7%;
  }
  .modal-content {
    margin: 0;
  }
  .section-gray {
    padding-bottom: 1rem;
  }
  .register-title {
    margin: 1rem;
  }
  .section-white {
    max-width: 95vw;
    padding: 15px;
  }
  .facebook-login-button {
    width: 100%;
    margin: 0;
  }
  .google-login-button {
    width: 100%;
    margin: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/Register/index.vue